import { useEffect, useState } from "react";
import api from "../lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TrendingUp } from "lucide-react";

const genTrend = (base) =>
  ["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((m, i) => ({
    month: m, price: Math.round(base * (1 + i * 0.02 + (i % 2 ? 0.008 : -0.004))),
  }));

const CITY_BASE = { Mumbai: 98000, Delhi: 62000, Bengaluru: 54000, Hyderabad: 43000, Pune: 38000, Chennai: 34000, Kolkata: 28000, Gurugram: 48000, Jaipur: 22000, Bhubaneswar: 16000, Goa: 45000, Ahmedabad: 26000 };

export default function MarketWatch() {
  const [ticker, setTicker] = useState([]);
  const [locations, setLocations] = useState([]);
  const [city, setCity] = useState("Mumbai");

  useEffect(() => {
    api.get("/ticker").then((r) => setTicker(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const data = genTrend(CITY_BASE[city] || 30000);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="market-watch-page">
      <p className="eyebrow">Live insights</p>
      <h1 className="font-heading mt-2 flex items-center gap-3 text-3xl font-bold tracking-tight">
        <TrendingUp className="h-8 w-8 text-emerald-600" /> Market Watch
      </h1>

      <div className="mt-6 overflow-hidden rounded-xl border bg-slate-900 py-3" data-testid="market-ticker">
        <div className="animate-ticker flex whitespace-nowrap">
          {[...ticker, ...ticker].map((t, i) => (
            <span key={i} className="mx-8 text-sm font-medium text-emerald-400">
              ▲ {t.text}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border bg-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-bold">Avg. price per sq.ft trend</h2>
            <p className="text-sm text-muted-foreground">6-month movement by city (indicative)</p>
          </div>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="market-city-select" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {locations.map((l) => <SelectItem key={l.city} value={l.city}>{l.city}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 h-72" data-testid="market-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [`₹${v.toLocaleString("en-IN")}/sq.ft`, city]} />
              <Area type="monotone" dataKey="price" stroke="#059669" strokeWidth={2.5} fill="url(#priceGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {ticker.map((t) => (
          <div key={t.ticker_id} className="rounded-xl border bg-card p-4 text-sm" data-testid={`ticker-item-${t.ticker_id}`}>
            <span className="font-price mr-2 text-emerald-600">▲</span>{t.text}
          </div>
        ))}
      </div>
    </div>
  );
}
